import { Component, OnInit, inject, DestroyRef, input } from '@angular/core';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '@core/auth/auth.service';
import { catchError, finalize, throwError } from 'rxjs';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { StorageService } from '@core/platform/storage.service';
import { HttpErrorResponse } from '@angular/common/http';
import {
  IUser,
  ILoginErrorResponse,
  ILoginTwoFactorRequired,
  LoginResponseStatusEnum,
  TwoFactorProviderEnum,
} from '@data/schema';
import { ToastrService } from 'ngx-toastr';
import { ElectronService } from '@core/platform/electron.service';
import { TokenService } from '@core/auth/token.service';
import { TwoFactorReminderService } from '@core/auth/two-factor-reminder.service';
import { environment } from '@env';
import { InputComponent, InputCheckboxComponent, ButtonComponent } from 'pi-ui';

import { takeUntilDestroyedSafe } from '@core/helpers/take-until-destroyed-safe';

@Component({
    selector: 'app-login-form',
    templateUrl: './login-form.component.html',
    styleUrls: ['./login-form.component.scss'],
    imports: [FormsModule, ReactiveFormsModule, InputComponent, TranslateModule, RouterLink, InputCheckboxComponent, ButtonComponent]
})
export class LoginFormComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastrService);
  private readonly twoFactorReminderService = inject(TwoFactorReminderService);

  login = false;
  twoFactorSubmitted = false;
  twoFactorSessionToken?: string;
  twoFactorProvider: TwoFactorProviderEnum = TwoFactorProviderEnum.AUTHENTICATOR;
  loginLoading = false;
  twoFactorLoading = false;
  emailLoading = false;

  private static readonly digitCodeValidators = [Validators.required, Validators.pattern(/^\d{6}$/)];
  private static readonly backupCodeValidators = [Validators.required];
  private static readonly digitCodeCleaner = /[^0-9]/g;
  private static readonly backupCodeCleaner = /[^0-9a-zA-Z-]/g;

  get twoFactorCodeCleaner(): RegExp {
    return this.isBackupCodeProvider ? LoginFormComponent.backupCodeCleaner : LoginFormComponent.digitCodeCleaner;
  }

  readonly formHeader = input.required<string>();

  /**
   * W Electronie nie ma wyboru: aplikacja desktopowa trzyma sesję we własnym
   * magazynie (pi-storage.electron przez IPC), więc checkbox jest ukryty,
   * a remember me jest zawsze włączone.
   */
  readonly isElectron = environment.electron;

  form = new FormGroup({
    username: new FormControl('', { nonNullable: true, validators: Validators.required }),
    password: new FormControl('', { nonNullable: true, validators: Validators.required }),
    isRememberMe: new FormControl(environment.electron, { nonNullable: true }),
  });

  twoFactorForm = new FormGroup({
    code: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.pattern(/^\d{6}$/)] }),
  });

  showResentInvitation = false;

  ngOnInit(): void {
    this.AutoLogin();
    this.form
      .get('username')
      ?.valueChanges?.pipe(takeUntilDestroyedSafe(this.destroyRef))
      ?.subscribe(() => {
        this.showResentInvitation = false;
      });
  }

  get loginFormControls() {
    return this.form.controls;
  }

  get twoFactorFormControls() {
    return this.twoFactorForm.controls;
  }

  get isTwoFactorStep(): boolean {
    return !!this.twoFactorSessionToken;
  }

  get isAuthenticatorProvider(): boolean {
    return this.twoFactorProvider === TwoFactorProviderEnum.AUTHENTICATOR;
  }

  get isBackupCodeProvider(): boolean {
    return this.twoFactorProvider === TwoFactorProviderEnum.BACKUP_CODE;
  }

  get isEmailProvider(): boolean {
    return this.twoFactorProvider === TwoFactorProviderEnum.EMAIL;
  }

  get twoFactorTitleKey(): string {
    switch (this.twoFactorProvider) {
      case TwoFactorProviderEnum.EMAIL:
        return 'login.two_factor.email_title';
      case TwoFactorProviderEnum.BACKUP_CODE:
        return 'login.two_factor.backup_code_title';
      default:
        return 'login.two_factor.authenticator_title';
    }
  }

  get twoFactorDescriptionKey(): string {
    switch (this.twoFactorProvider) {
      case TwoFactorProviderEnum.EMAIL:
        return 'login.two_factor.email_description';
      case TwoFactorProviderEnum.BACKUP_CODE:
        return 'login.two_factor.backup_code_description';
      default:
        return 'login.two_factor.authenticator_description';
    }
  }

  get twoFactorCodeErrorKey(): string {
    return this.isBackupCodeProvider ? 'login.two_factor.backup_code_error' : 'login.two_factor.code_error';
  }

  resentInvitation(event: MouseEvent) {
    event.preventDefault();

    const email = this.form.get('username')?.value;

    if (email) {
      this.authService.resendInvitation(email).subscribe(() => {
        this.showResentInvitation = false;
        this.toast.info(this.translate.instant('login.resent-invitation-success'));
      });
    }

    event.stopPropagation();
    return false;
  }

  submit() {
    if (this.isTwoFactorStep) {
      this.submitTwoFactorCode();
      return;
    }

    this.login = true;
    if (this.form.valid) {
      this.loginLoading = true;
      TokenService.setIsRememberMe(this.form.controls.isRememberMe.value);
      this.authService
        .logIn(this.form.controls.username.value, this.form.controls.password.value)
        .pipe(
          catchError((err) => {
            if (err instanceof HttpErrorResponse && (err.error as ILoginErrorResponse)?.responseStatus) {
              switch ((err.error as ILoginErrorResponse)?.responseStatus) {
                case LoginResponseStatusEnum.USER_NOT_REGISTERED:
                  this.showResentInvitation = true;
                  break;
                default:
                  this.form.setErrors({
                    login: this.translate.instant('login.form.error_message'),
                  });
                  break;
              }
            } else if (err) {
              this.form.setErrors({
                login: this.translate.instant('login.form.error_message'),
              });
            }

            this.form.updateValueAndValidity();
            return throwError(() => err);
          }),
          finalize(() => {
            this.loginLoading = false;
          })
        )
        .subscribe((res) => {
          if (this.isTwoFactorRequired(res)) {
            this.openTwoFactorStep(res.sessionToken);
            return;
          }

          this.completeSuccessfulLogin(res);
        });
    } else {
      this.validateAllFormFields(this.form);
    }
  }

  submitTwoFactorCode(): void {
    this.twoFactorSubmitted = true;

    if (this.twoFactorForm.invalid || this.twoFactorLoading || !this.twoFactorSessionToken) {
      this.validateAllFormFields(this.twoFactorForm);
      return;
    }

    this.twoFactorLoading = true;

    this.authService
      .verifyTwoFactorCode({
        code: this.twoFactorForm.controls.code.value,
        sessionToken: this.twoFactorSessionToken,
        provider: this.twoFactorProvider,
      })
      .pipe(
        catchError((err) => {
          this.twoFactorForm.setErrors({
            login: this.translate.instant('login.two_factor.error_message'),
          });
          this.twoFactorForm.updateValueAndValidity();
          return throwError(() => err);
        }),
        finalize(() => {
          this.twoFactorLoading = false;
        })
      )
      .subscribe((user) => {
        this.completeSuccessfulLogin(user);
      });
  }

  useEmailCode(event: MouseEvent): false {
    event.preventDefault();

    if (!this.twoFactorSessionToken || this.emailLoading) {
      return false;
    }

    this.emailLoading = true;

    this.authService
      .sendTwoFactorEmail(this.twoFactorSessionToken)
      .pipe(
        catchError((err) => {
          this.toast.error(this.translate.instant('login.two_factor.email_error'));
          return throwError(() => err);
        }),
        finalize(() => {
          this.emailLoading = false;
        })
      )
      .subscribe((response) => {
        if (response.success) {
          this.setTwoFactorProvider(TwoFactorProviderEnum.EMAIL);
          this.toast.info(this.translate.instant('login.two_factor.email_sent'));
          return;
        }

        this.toast.error(this.translate.instant('login.two_factor.email_error'));
      });

    return false;
  }

  useAuthenticatorCode(event: MouseEvent): false {
    event.preventDefault();
    this.setTwoFactorProvider(TwoFactorProviderEnum.AUTHENTICATOR);
    return false;
  }

  useBackupCode(event: MouseEvent): false {
    event.preventDefault();
    this.setTwoFactorProvider(TwoFactorProviderEnum.BACKUP_CODE);
    return false;
  }

  backToCredentials(event: MouseEvent): false {
    event.preventDefault();
    this.twoFactorSessionToken = undefined;
    this.setTwoFactorProvider(TwoFactorProviderEnum.AUTHENTICATOR);
    return false;
  }

  private openTwoFactorStep(sessionToken: string): void {
    this.twoFactorSessionToken = sessionToken;
    this.setTwoFactorProvider(TwoFactorProviderEnum.AUTHENTICATOR);
  }

  private setTwoFactorProvider(provider: TwoFactorProviderEnum): void {
    this.twoFactorProvider = provider;
    this.twoFactorForm
      .get('code')
      ?.setValidators(
        provider === TwoFactorProviderEnum.BACKUP_CODE
          ? LoginFormComponent.backupCodeValidators
          : LoginFormComponent.digitCodeValidators
      );
    this.resetTwoFactorCode();
  }

  private resetTwoFactorCode(): void {
    this.twoFactorSubmitted = false;
    this.twoFactorForm.reset();
    this.twoFactorForm.setErrors(null);
    this.twoFactorForm.get('code')?.updateValueAndValidity();
  }

  private isTwoFactorRequired(res: unknown): res is ILoginTwoFactorRequired {
    return !!res && (res as ILoginTwoFactorRequired).twoFactorRequired === true;
  }

  private completeSuccessfulLogin(user: IUser): void {
    // Link `#accid=` może wymagać przełączenia konta i powrotu na docelowy URL -
    // wtedy pomijamy domyślną nawigację na /dashboard.
    if (this.authService.applyPendingAccountSwitchIfAny(true)) {
      this.twoFactorReminderService.handleAfterLogin(user);
      return;
    }
    this.router.navigate(['/dashboard']).finally(() => {
      this.twoFactorReminderService.handleAfterLogin(user);
    });
  }

  validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  AutoLogin() {
    const accessTokenObj = ElectronService.getToken();
    const rememberMe = StorageService.local.getItem('isRememberMe');
    if (accessTokenObj && rememberMe == 'yes') {
      this.router.navigate(['/dashboard']);
    }
  }

  showErrors() {
    const errors = this.form.errors;
    if (errors) {
      return Object.values(errors).join('<br />');
    }
    return;
  }

  showTwoFactorErrors() {
    const errors = this.twoFactorForm.errors;
    if (errors) {
      return Object.values(errors).join('<br />');
    }
    return;
  }
}
